'use client';

import type { UserItemDates } from '@/src/types/listingDetails.type';
import { useQuery, useQueryClient } from '@zg-rentals/react-query5';
import type { HpDisplayTagDto } from '@zg-rentals/ts-hotpads-api-client';
import { listing } from '@/src/lib/api/endpoints/listing';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import type { ModelSummary } from '@/src/components/ListingCard/types';
import { listingDetailKeys, normalizeListingDetailUnit } from '@/src/lib/hooks/query/queryKeys';
import { adaptV3SimilarListings, getEmbeddedV3SimilarListings } from '@/src/lib/utils/v3ListingDetail';
import type { ListingMinMaxPriceBeds } from '@/src/types/commonTypes.type';

type SimilarListingModel = {
  numBeds?: number;
  beds?: number | string;
  low?: number | string | null;
  high?: number | string | null;
  lowPrice?: number | string | null;
  highPrice?: number | string | null;
};

type SimilarListingCondensedAmenities = {
  pets?: string | boolean | null;
  laundry?: string | boolean | null;
  security?: string | boolean | null;
  airConditioning?: boolean | null;
  dishwasher?: boolean | null;
  parking?: boolean | null;
  gym?: boolean | null;
  pool?: boolean | null;
};

export type SimilarListing = {
  maloneLotIdEncoded?: string;
  lotIdEncoded?: string;
  aliasEncoded?: string;
  unit?: string;
  uriV2?: string;
  uriMalone?: string;
  uriBuilding?: string;
  displayName?: string;
  name?: string;
  title?: string;
  listingType?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    hideStreet?: boolean;
  };
  pricing?: { summary?: { priceLow?: number; priceHigh?: number } };
  listingMinMaxPriceBeds?: Partial<ListingMinMaxPriceBeds>;
  modelSummary?: ModelSummary;
  photos?: Array<{ url?: string }>;
  photoCount?: number;
  medPhotoUrl?: string;
  medPhotoUrls?: Array<string>;
  photoUrl?: string;
  previewPhoto?: { url?: string };
  previewPhotoMed?: { url?: string };
  thumbPhoto?: { url?: string };
  thumbPhotoUrl?: string;
  // Comparison-table fields populated when the real fetch is wired up
  // (currently the hook stub returns []).
  active?: boolean;
  priority?: number;
  propertyType?: string;
  rentalApplicationStatus?: string;
  score?: number;
  searchKeyword?: string;
  isDemoted?: boolean;
  isPaidAndNotDemoted?: boolean;
  isPaidMultifamily?: boolean;
  trusted?: boolean;
  userItemTypes?: Array<string>;
  userItemDates?: Partial<UserItemDates>;
  isAllowedToRetain?: boolean;
  hasTotalMonthlyPrice?: boolean;
  displayTags?: Array<HpDisplayTagDto>;
  geo?: { lat?: number; lon?: number };
  models?: Array<SimilarListingModel>;
  modelsAndPricing?: Array<SimilarListingModel>;
  amenities?: { condensedAmenities?: SimilarListingCondensedAmenities };
};

export const similarListingsKeys = {
  all: ['halo', 'similarListings'] as const,
  byMaloneLotId: (maloneLotIdEncoded: string | null, unit: string | null = null) =>
    ['halo', 'similarListings', 'byMaloneLotId', maloneLotIdEncoded ?? '', unit ?? ''] as const,
};

export function useSimilarListings(params: { maloneLotIdEncoded: string | null | undefined; unit?: string | null }) {
  const { maloneLotIdEncoded, unit = null } = params;
  const normalizedUnit = normalizeListingDetailUnit(unit);
  const queryClient = useQueryClient();
  const cachedListing = maloneLotIdEncoded
    ? (queryClient.getQueryData<PropertyDetailListing>(
        listingDetailKeys.byMaloneLotId(maloneLotIdEncoded, 'unit', normalizedUnit),
      ) ??
      queryClient.getQueryData<PropertyDetailListing>(
        listingDetailKeys.byMaloneLotId(maloneLotIdEncoded, 'building', normalizedUnit),
      ) ??
      queryClient.getQueryData<PropertyDetailListing>(listingDetailKeys.byMaloneLotId(maloneLotIdEncoded, 'unit')) ??
      queryClient.getQueryData<PropertyDetailListing>(listingDetailKeys.byMaloneLotId(maloneLotIdEncoded, 'building')))
    : undefined;
  const embeddedSimilarListings = getEmbeddedV3SimilarListings(cachedListing);

  return useQuery<Array<SimilarListing>>({
    queryKey: similarListingsKeys.byMaloneLotId(maloneLotIdEncoded ?? null, normalizedUnit),
    initialData: embeddedSimilarListings,
    queryFn: async () => {
      if (!maloneLotIdEncoded) return [];
      return adaptV3SimilarListings(await listing.fetchListingDetailV3(maloneLotIdEncoded, { unit: normalizedUnit }));
    },
    enabled: Boolean(maloneLotIdEncoded) && !embeddedSimilarListings,
    staleTime: 5 * 60 * 1000,
    meta: {
      tags: ['similarListings'],
      context: {
        api: 'hotpads-api',
        details: 'fetch similar listings',
      },
    },
  });
}
